import React from 'react'
import { Container, Row, Col, Form } from 'reactstrap'
import { Control, FieldValues, useForm } from 'react-hook-form'
import ICSDateRange from '../../../components/ICSDateRange/ICSDateRange'
import ComponentBox from '../../../components/ComponentBox/ComponentBox'
import { useDispatch } from 'react-redux'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import { getAllResults } from '../../../redux/slices'
import AnalyzerTypeSelector from './AnalyzerTypeSelector'
import AnalyzerPicker from './AnalyzerPicker'
import { useTimezone } from '../../../hooks/useTimezone'
import ICSCheckBoxInput from '../../../components/ICSCheckBoxInput/ICSCheckBoxInput'
import { ResultsSearchValues, ResultsSearchQuery } from '../types'
import './SearchBox.css'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { AppDispatch } from '../../../services/lib'
import {
  setPageSize,
  setPageIndex,
  setPendingPageIndex,
  RESULTS_CHUNK_SIZE
} from '../../../redux/slices/resultsSlice'

const PAGE_SIZE_OPTIONS = [25, 50, 100]

type SearchBoxProps = {
  isAdmin: boolean
  currentUserId: number | null
}

const SearchBox: React.FC<SearchBoxProps> = ({ isAdmin, currentUserId }) => {
  const { formatUtcTimeFromLocal } = useTimezone()
  const dispatch: AppDispatch = useDispatch()
  const currentPageSize =
    useAppSelector((state) => state.results.pageSize) || PAGE_SIZE_OPTIONS[1]
  const {
    handleSubmit,
    control,
    register,
    getValues,
    formState: { isSubmitting, isValid, isDirty }
  } = useForm<ResultsSearchValues>({
    mode: 'onChange',
    defaultValues: {
      dateRange: {
        start: null,
        end: null
      },
      analyzerType: null,
      analyzerId: null,
      includeAllUsers: false,
      pageSize: currentPageSize,
      hasResults: false
    }
  })
  const isFormValid = isValid

  const searchData = (data: ResultsSearchValues) => {
    const req: ResultsSearchQuery = {
      analyzer_type: data.analyzerType ?? undefined,
      analyzer_id: data.analyzerId ?? undefined,
      start: formatUtcTimeFromLocal(data.dateRange.start) ?? undefined,
      end: formatUtcTimeFromLocal(data.dateRange.end) ?? undefined,
      offset: 0,
      max: RESULTS_CHUNK_SIZE
    }

    if (data.hasResults) {
      req.hasResults = true
    }

    if (isAdmin) {
      if (data.includeAllUsers) {
        req.includeAllUsers = true
      } else if (currentUserId !== null) {
        req.user_id = currentUserId
      }
    } else if (currentUserId !== null) {
      req.user_id = currentUserId
    }

    dispatch(setPageSize(data.pageSize))
    dispatch(setPageIndex(0))
    dispatch(setPendingPageIndex(null))
    dispatch(
      getAllResults({
        query: _toApiQuery(req),
        meta: { desiredPageIndex: 0, replace: true }
      })
    )
  }

  return (
    <Container fluid={true}>
      <Form onSubmit={handleSubmit(searchData)}>
        <ComponentBox
          isCustom={true}
          cardClass="groups-results-container"
          cardBodyId="groupsCardBody"
          title="Search Criteria"
          accordion={true}
        >
          <Row>
            <Col xs={12} md={6}>
              <div>
                <div className="spacer">
                  <h4>Date Range</h4>
                  <ICSDateRange name="dateRange" control={control} />
                </div>
              </div>

              <div className="spacer">
                <h4>Additional Options</h4>
                <ICSCheckBoxInput
                  name="hasResults"
                  control={control as unknown as Control<FieldValues>}
                  errors={{}}
                  label="Only show results with files"
                  defaultValue={getValues('hasResults')}
                />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div>
                <div className="spacer">
                  <h4>Analyzer Type</h4>
                  <AnalyzerTypeSelector control={control} name="analyzerType" />
                </div>
                <div className="spacer">
                  <h4>Analyzer</h4>
                  <AnalyzerPicker control={control} name="analyzerId" />
                </div>
              </div>
            </Col>
          </Row>
          <Row className="dataSearchButtonsRow">
            <Col className="dataSearchButtonCol" />
            <Col className="col-sm-auto dataSearchButtonCol">
              <ICSButton
                className="dataSearchButton"
                icon="search"
                text="Search"
                type="success"
                onClick={handleSubmit(searchData)}
                disabled={isSubmitting || !isFormValid || !isDirty}
              />
              <select
                className="ml-1 dataSearchMaxSelect form-control"
                {...register('pageSize', { valueAsNumber: true })}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={`resultsPagePageSize${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
        </ComponentBox>
      </Form>
    </Container>
  )
}

export default SearchBox

const _toApiQuery = (
  query: ResultsSearchQuery
): Record<string, string | number> => {
  return Object.entries(query).reduce<Record<string, string | number>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc
      }

      if (typeof value === 'boolean') {
        acc[key] = value ? 'true' : 'false'
      } else {
        acc[key] = value as string | number
      }

      return acc
    },
    {}
  )
}
